import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRotateLeft,
  faChevronLeft,
  faInfoCircle,
} from '@rq-ratings/pro-solid-svg-icons'
import React from 'react'
import { Button } from 'react-bootstrap'
import { useNavigate } from 'react-router'

import { ImportReferralsProvider } from '../../../contexts/ImportReferralsContext'
import useAppDispatch from '../../../hooks/useAppDispatch'
import useAppSelector from '../../../hooks/useAppSelector'
import useMaxWidth from '../../../hooks/useMaxWidth'
import { useUpdateOnboardingChecklistMutation } from '../../../hooks/useUpdateOnboardingChecklistMutation'
import { generateUUID } from '../../../lib/helpers/uuid'
import { ROUTES } from '../../../lib/routes'
import { selectOnboardingChecklist } from '../../../redux/slices/onboardingChecklist'
import { initialReferralsImportItemState } from '../../../redux/slices/referralsImportBase'
import {
  selectReferralsImportHistorical,
  setImportItems,
  setIsOpen,
  setStep,
} from '../../../redux/slices/referralsImportHistorical'
import { BulkImportReferralCollection } from '../../../types/responses/referral-import'
import ResolveImportHistorical from './ResolveImportHistorical'
import UploadHistorical from './UploadHistorical'

interface Props {
  children: (renderProps: RenderProps) => React.ReactNode
}

interface RenderProps {
  renderHeader: (faIcon?: IconProp) => React.ReactNode
  renderBody: () => React.ReactNode
  renderFooter: () => React.ReactNode
  isOnResolveStep: boolean
}

const ImportHistoricalReferralsFlow: React.FC<Props> = ({ children }) => {
  const isMaxWidthMd = useMaxWidth('md')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const referralsImportHistoricalState = useAppSelector(
    selectReferralsImportHistorical,
  )
  const updateChecklistMutation = useUpdateOnboardingChecklistMutation()
  const checklist = useAppSelector(selectOnboardingChecklist)

  function handleSuccessfulUpload(data: BulkImportReferralCollection) {
    dispatch(
      setImportItems(
        data['hydra:member'].map((item) => ({
          ...initialReferralsImportItemState,
          ...item,
          refUUID: generateUUID(),
          onClientDuplicate: referralsImportHistoricalState.onClientDuplicate,
        })),
      ),
    )
    dispatch(setStep('resolve-import'))
  }

  function handleStartOver() {
    dispatch(setStep(referralsImportHistoricalState.initialStep))
    dispatch(setImportItems([]))
  }

  function handleFinish() {
    dispatch(setStep(referralsImportHistoricalState.initialStep))
    dispatch(setImportItems([]))
    dispatch(setIsOpen(false))

    markCompletedInOnboardingChecklist()
  }

  function handleAutomaticFinish() {
    dispatch(setStep(referralsImportHistoricalState.initialStep))
    dispatch(setImportItems([]))
    dispatch(setIsOpen(false))

    navigate(ROUTES.referrals)

    markCompletedInOnboardingChecklist()
  }

  function markCompletedInOnboardingChecklist() {
    // Don't bother doing anything if it's already complete
    if (checklist?.uploadHistoricalReferrals === false) {
      updateChecklistMutation.mutate({
        checklistId: checklist?.id,
        request: { uploadHistoricalReferrals: true },
      })
    }
  }

  const FinishButton = (
    <Button variant="success" onClick={handleFinish} size="lg">
      Finish
    </Button>
  )

  const StartOverButton = (
    <Button
      variant="link"
      size="lg"
      onClick={handleStartOver}
      className="ps-0 d-flex gap-1 align-items-center"
    >
      <FontAwesomeIcon icon={faArrowRotateLeft} />
      Start over
    </Button>
  )

  const fullscreen =
    isMaxWidthMd ||
    (referralsImportHistoricalState.step === 'resolve-import'
      ? true
      : undefined)

  switch (referralsImportHistoricalState.step) {
    case 'upload':
      return children({
        renderHeader: (faIcon) => (
          <div className="d-flex gap-3 align-items-center text-black">
            {faIcon && <FontAwesomeIcon icon={faIcon} />}
            Import historical referrals
          </div>
        ),
        renderBody: () => (
          <>
            {referralsImportHistoricalState.importItems.length > 0 && (
              <div className="alert alert-warning p-2 gap-2 align-items-start">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="text-warning mt-1"
                />

                <span>
                  You have{' '}
                  {referralsImportHistoricalState.importItems.length == 1
                    ? '1 referral'
                    : `${referralsImportHistoricalState.importItems.length} referrals`}{' '}
                  in progress.{' '}
                  <a
                    href="#"
                    onClick={() => dispatch(setStep('resolve-import'))}
                  >
                    Continue
                  </a>{' '}
                  or upload a new file below to start over.
                </span>
              </div>
            )}

            <ImportReferralsProvider>
              <UploadHistorical
                onSuccessfulUpload={handleSuccessfulUpload}
                className="mt-3"
              />
            </ImportReferralsProvider>
          </>
        ),
        renderFooter: () => (
          <>
            {StartOverButton}
            <div className="flex-grow-1"></div>
            {FinishButton}
          </>
        ),
        isOnResolveStep: false,
      })

    case 'resolve-import':
      return children({
        renderHeader: (faIcon) => (
          <div>
            <button
              className="bg-white border-0"
              // style={{ left: '-30px' }}
              onClick={() => dispatch(setStep('upload'))}
              css={{ ':hover': { color: 'var(--bs-primary) !important' } }}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            {faIcon && <FontAwesomeIcon icon={faIcon} className="me-2" />}
            Import historical referrals - Review
          </div>
        ),
        renderBody: () => (
          <>
            <ResolveImportHistorical
              startOverButton={StartOverButton}
              handleFinish={handleFinish}
              handleAutomaticFinish={handleAutomaticFinish}
            />

            {fullscreen && (
              <div
                style={{
                  height: 82,
                  borderTop:
                    'var(--bs-modal-header-border-width) solid var(--bs-modal-header-border-color)',
                }}
              />
            )}
          </>
        ),
        renderFooter: () => null,
        isOnResolveStep: true,
      })

    default:
      throw `Invalid import step ${referralsImportHistoricalState.step}`
  }
}

export default ImportHistoricalReferralsFlow
